import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { KeyRound, Mail, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const { refresh } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [devNote, setDevNote] = useState(null);

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot/start", { email });
      if (r.data?.dev_code) setDevNote(r.data);
      toast.success(t("toast.emailSent"));
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error(t("toast.enter6"));
    if (newPassword.length < 6) return toast.error(t("toast.pwdShort"));
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot/reset", { email, code, new_password: newPassword });
      setToken(r.data.token);
      await refresh();
      toast.success(t("toast.pwdUpdated"));
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] paper-bg grid place-items-center px-4 py-12">
      <div className="w-full max-w-md card-classic p-8" data-testid="forgot-page">
        {step === 1 ? (
          <>
            <div className="pill mb-3"><KeyRound size={12}/> {t("forgot.badge")}</div>
            <h1 className="font-serif-display text-3xl font-bold text-[#0F172A]">{t("forgot.title")}</h1>
            <p className="text-[#475569] mt-1">{t("forgot.subtitle")}</p>
            <form onSubmit={sendCode} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input id="email" type="email" required value={email}
                  onChange={(e)=>setEmail(e.target.value)}
                  data-testid="forgot-email-input" className="mt-1"/>
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-[#0F172A] hover:bg-[#1E293B] h-11"
                data-testid="forgot-send-code-button">
                {busy ? (<><Loader2 size={16} className="mr-2 animate-spin"/> {t("forgot.sending")}</>) : (<><Mail size={16} className="mr-2"/> {t("forgot.send")}</>)}
              </Button>
              <div className="text-center text-sm">
                <Link to="/login" className="text-[#475569] underline">{t("forgot.back")}</Link>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="pill mb-3"><Mail size={12}/> {t("forgot.step2.badge")}</div>
            <h1 className="font-serif-display text-3xl font-bold text-[#0F172A]">{t("forgot.step2.title")}</h1>
            <p className="text-[#475569] mt-1">{t("forgot.step2.subtitle")} <strong>{email}</strong> {t("forgot.step2.subtitle2")}</p>

            {devNote?.dev_code && (
              <div className="mt-4 p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900" data-testid="dev-code-banner">
                <strong>{t("register.devNote")}</strong> <span className="font-mono-jb text-base ml-1">{devNote.dev_code}</span>
              </div>
            )}

            <form onSubmit={reset} className="mt-6 space-y-5">
              <div className="flex justify-center" dir="ltr" data-testid="forgot-code-otp">
                <InputOTP maxLength={6} value={code} onChange={setCode}>
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => (
                      <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl font-serif-display" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <div>
                <Label htmlFor="newpwd">{t("auth.newPassword")}</Label>
                <Input id="newpwd" type="password" required value={newPassword}
                  onChange={(e)=>setNewPassword(e.target.value)}
                  data-testid="forgot-new-password-input" className="mt-1"/>
              </div>

              <Button type="submit" disabled={busy || code.length !== 6}
                className="w-full bg-[#0F172A] hover:bg-[#1E293B] h-11"
                data-testid="forgot-reset-button">
                {busy ? t("forgot.step2.submitting") : t("forgot.step2.submit")}
              </Button>

              <div className="text-center text-sm">
                <button type="button" onClick={() => setStep(1)} className="text-[#475569] underline"
                  data-testid="forgot-back-button">
                  {t("register.step2.changeEmail")}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
